import React, { useCallback, useEffect, useState } from 'react';
import QuestionCard from './QuestionCard.jsx';
import ContextModal from './ContextModal.jsx';
import CategoryIcon from './CategoryIcon.jsx';
import SectionInterestPicker from './SectionInterestPicker.jsx';
import { fetchSectionForCategory, fetchAvailableSectionCategories } from '../data/sections.js';
import { saveSectionPreferences } from '../data/profile.js';
import { submitDailySet } from '../util/playContract.js';

// Back Sections for a player who did NOT arrive from a category challenge.
//
// The shape (Tom 2026-07-28), and the ordering of these two steps is the whole
// point: submit the six FIRST, then "What are you interested in?", and only
// then any section questions. Asking someone to rank six categories before
// they've played is a tax on activation; asking after they've submitted is a
// natural "want more?" beat, and the answer routes the extra questions.
//
// A category-challenged arrival never reaches this component — their sections
// are interleaved into the ballot itself (util/focusedBallot.js), because their
// friend already chose the arena for them.
//
// Sections pay like the six since 2026-08-01 (+1 per answer at submission,
// +6 per correct at resolution — all server-side; the retired FP-2 zero-coin
// guard is gone). Section double-downs remain expressive-only (D4), so no 2x
// chip is offered here.

export default function BackSections({ uid, initialPreferences, onPreferencesSaved }) {
  // 'picking' → the interest picker; 'playing' → this category's questions.
  const hasPrefs = Array.isArray(initialPreferences) && initialPreferences.length > 0;
  const [mode, setMode] = useState(hasPrefs ? 'playing' : 'picking');
  const [prefs, setPrefs] = useState(initialPreferences || []);
  const [available, setAvailable] = useState(null); // null = loading
  const [category, setCategory] = useState(null);
  const [questions, setQuestions] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  // The context modal's target INDEX (web parity W2) — an index, not a
  // snapshot, so the modal renders the live question after in-modal answers.
  const [contextIndex, setContextIndex] = useState(null);

  // Which categories actually have a section published today. Without this, a
  // user whose top pick has no section today would be routed into an empty
  // room — worse than not offering it.
  useEffect(() => {
    let alive = true;
    fetchAvailableSectionCategories()
      .then((cats) => alive && setAvailable(cats))
      .catch(() => alive && setAvailable([]));
    return () => {
      alive = false;
    };
  }, []);

  // Resolve which section to open: the user's HIGHEST-priority preference that
  // has questions today (top pick first — that's what the saved order means).
  useEffect(() => {
    if (mode !== 'playing' || !available) return;
    const chosen = prefs.find((c) => available.includes(c)) || null;
    setCategory(chosen);
    if (!chosen) {
      setQuestions([]);
      return;
    }
    let alive = true;
    fetchSectionForCategory(chosen, uid)
      .then((qs) => alive && setQuestions(qs))
      .catch(() => alive && setQuestions([]));
    return () => {
      alive = false;
    };
  }, [mode, available, prefs, uid]);

  const handleSavePrefs = useCallback(
    (next) => {
      setPrefs(next);
      saveSectionPreferences(uid, next);
      onPreferencesSaved?.(next);
    },
    [uid, onPreferencesSaved]
  );

  const handleAnswer = useCallback((index, answer) => {
    setQuestions((prev) =>
      prev.map((q, i) => (i === index ? { ...q, answer, isSkipped: false } : q))
    );
  }, []);

  const answered = (questions || []).filter((q) => typeof q.answer === 'boolean').length;
  const total = (questions || []).length;

  const handleSubmit = useCallback(async () => {
    if (!questions?.length || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      // Identical write path to the six: users/{uid}/answers/{qid} +
      // questions/{qid}/votes/{uid}. Safe because `awardSubmissionPoints`
      // counts votes ONLY on the ids in dailySets/{today}.questionIds — section
      // votes are invisible to the all-six award gate, so this can neither
      // double-award nor satisfy the six on its own.
      await submitDailySet(uid, questions);
      setSubmitted(true);
    } catch (e) {
      console.error('[sections] submit failed:', e);
      setError('Could not save those. Check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  }, [questions, submitting, uid]);

  if (mode === 'picking') {
    return (
      <div className="ps-card" style={{ marginTop: 16 }}>
        <SectionInterestPicker
          initial={prefs}
          onSave={handleSavePrefs}
          onDone={() => setMode('playing')}
        />
      </div>
    );
  }

  // Still resolving what's available / fetching — render nothing rather than a
  // spinner. This sits BELOW a completed six; a loading shell here would pull
  // attention away from the submission celebration above it.
  if (available === null || questions === null) return null;

  // Nothing to offer: no section today in any category they follow. Silent —
  // an empty "no extra questions" card would be pure noise.
  if (!category || total === 0) return null;

  if (submitted) {
    return (
      <div className="ps-card ps-center" style={{ marginTop: 16 }}>
        <p style={{ margin: 0, fontWeight: 800, fontSize: 16 }}>
          {category} locked in
        </p>
        <p className="ps-muted" style={{ margin: '6px 0 0', fontSize: 13.5 }}>
          They resolve with everything else after 5pm ET.
        </p>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 20 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          margin: '0 0 10px',
          padding: '0 2px',
        }}
      >
        <CategoryIcon category={category} size={20} />
        <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>
          More {category} questions
        </h3>
      </div>
      <p className="ps-muted" style={{ margin: '0 0 12px', fontSize: 13.5, lineHeight: 1.45 }}>
        Three extra calls from today&rsquo;s {category} section. Optional — they sharpen
        your record, and they&rsquo;re what head-to-head games in this category are
        scored on.
      </p>

      {questions.map((q, i) => (
        <QuestionCard
          key={q.id}
          question={q}
          index={i}
          disabled={submitting}
          onAnswer={(ans) => handleAnswer(i, ans)}
          onContext={() => setContextIndex(i)}
        />
      ))}

      {contextIndex !== null && questions[contextIndex] && (
        <ContextModal
          question={questions[contextIndex]}
          onAnswer={submitting ? undefined : (ans) => handleAnswer(contextIndex, ans)}
          onClose={() => setContextIndex(null)}
        />
      )}

      {error && (
        <p
          role="alert"
          style={{
            margin: '0 0 8px',
            color: 'var(--danger)',
            fontWeight: 700,
            fontSize: 13,
            textAlign: 'center',
          }}
        >
          {error}
        </p>
      )}
      <button
        onClick={handleSubmit}
        disabled={answered === 0 || submitting}
        style={{
          width: '100%',
          padding: '15px 0',
          borderRadius: 'var(--radius-sm)',
          border: 'none',
          background: answered > 0 ? 'var(--gold)' : 'var(--surface)',
          color: answered > 0 ? '#1a1a1a' : 'var(--secondary-text)',
          fontWeight: 800,
          fontSize: 16,
          cursor: answered > 0 && !submitting ? 'pointer' : 'default',
        }}
      >
        {submitting
          ? 'Saving…'
          : answered > 0
            ? `Lock in ${answered} more`
            : `${total} extra questions`}
      </button>
    </div>
  );
}
